import { useAtomValue } from "jotai";
import { useEffect, useState, useRef } from "react";
import { queriesEnabledAtom } from "../../atoms/GlobalAtoms";

/**
 * The Goal of this hook is to keep the user logged in while they are active
 * We can achieve this by using the refresh token to fetch a new auth and refresh token
 * before the refresh token expires.
 *
 * isActive - State variable storing whether or not the user is active
 *
 * This hook uses 2 timers (setTimeouts)
 * A timer to refetch tokens using the refresh token
 * And an Idle Timer to keep track of when user is inactive (idle)
 *
 * The Idle Timer sets the isActive state to false when/if it finishes
 * The Idle Timer restarts every time a user activity event fires (mousedown, keydown, visibility)
 *
 * The refresh token timer will not refresh the token if the isActive state is false
 * The only way isActive will be false is if the idle timer finishes
 *
 * getRefreshTokenRef - React.useRef storing the function to refetch the tokens using the refresh token.
 */
const useAutoRefresh = (getRefreshTokenRef: any) => {
  // Whether or not the user is active
  const [isActive, setIsActive] = useState(false);
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const queriesEnabledRef = useRef<boolean>(queriesEnabled);
  const isActiveRef = useRef<boolean>(isActive);
  isActiveRef.current = isActive;
  queriesEnabledRef.current = queriesEnabled;

  // Timer id refs
  const refreshTimerRef = useRef<NodeJS.Timeout>();
  const idleTimerRef = useRef<NodeJS.Timeout>();

  const startIdleTimer = () => {
    clearTimeout(idleTimerRef.current);
    setIsActive(true);

    const idleTimer = setTimeout(() => {
      setIsActive(false);
    }, 1_620_000); // 1.62m milliseconds = 27 minutes

    idleTimerRef.current = idleTimer;
  };

  const startRefreshTokenTimer = () => {
    clearTimeout(refreshTimerRef.current);

    const originalRefreshTokenTimer = setTimeout(() => {
      if (isActiveRef.current && queriesEnabledRef.current) {
        getRefreshTokenRef.current();
        startRefreshTokenTimer();
      }
    }, 1_620_000); // 1.62m milliseconds = 27 minutes.
    // This is preset as we fetch a refresh token every first render of the application

    refreshTimerRef.current = originalRefreshTokenTimer;
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible" && queriesEnabledRef.current) {
      getRefreshTokenRef.current();
    }

    startIdleTimer();
  };

  useEffect(() => {
    startIdleTimer();
    startRefreshTokenTimer();

    document.addEventListener("click", startIdleTimer);
    document.addEventListener("keydown", startIdleTimer);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("click", startIdleTimer);
      document.removeEventListener("keydown", startIdleTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);
};

export default useAutoRefresh;
